import { useState, useMemo, useEffect } from 'react';
import { getColumnType } from '../utils/formatters';

export const useTableState = ({
  rows,
  columnOrder,
  hiddenCols,
  pinnedCols,
  enableFiltering,
  enableGlobalSearch,
  enableSorting,
  sortConfig,
  columnFilters,
  globalQuery,
  rowsPerPage,
  isMobile,
  serverPagination = false,
  serverTotalRows = 0
}) => {
  const [page, setPage] = useState(0);
  const [expandedRowKey, setExpandedRowKey] = useState(null);
  const [isPaginationChange, setIsPaginationChange] = useState(false);

  // ✅ Reset page to 0 when sorting, filters or search change
  useEffect(() => {
    setPage(0);
  }, [sortConfig, columnFilters, globalQuery]);

  // Derive visible columns with pinning and custom order
  const orderedKeysDesktop = useMemo(() => {
    if (!Array.isArray(columnOrder)) return [];
    
    // 1. Filtrar las columnas que no están ocultas
    const visible = columnOrder.filter(k => !hiddenCols?.has?.(k));
    
    // 2. Separar en fijas y no fijas
    const pinned = visible.filter(k => pinnedCols?.has?.(k));
    const unpinned = visible.filter(k => !pinnedCols?.has?.(k));

    // 3. Unir (fijas primero, luego el resto)
    return [...pinned, ...unpinned];
  }, [columnOrder, hiddenCols, pinnedCols]);

  // Filtering
  const filteredData = useMemo(() => {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    if (!enableFiltering && !enableGlobalSearch) return rows;

    const safeColumnOrder = Array.isArray(columnOrder) ? columnOrder : [];

    return rows.filter(row => {
      // Column Filters
      if (enableFiltering && columnFilters) {
        for (const col of Object.keys(columnFilters)) {
          const filterVal = (columnFilters[col] || '').trim().toLowerCase();
          if (!filterVal) continue;
          const cellVal = String(row[col] ?? '').toLowerCase();
          if (!cellVal.includes(filterVal)) return false;
        }
      }
      
      // Global Query
      if (enableGlobalSearch && globalQuery && safeColumnOrder.length > 0) {
        const query = globalQuery.trim().toLowerCase();
        if (query) {
          const matchesGlobal = safeColumnOrder.some(key => {
            const val = String(row[key] ?? '').toLowerCase();
            return val.includes(query);
          });
          if (!matchesGlobal) return false;
        }
      }
      return true;
    });
  }, [rows, columnFilters, globalQuery, enableFiltering, enableGlobalSearch, columnOrder]);

  // Sorting
  const sortedData = useMemo(() => {
    if (!enableSorting || !sortConfig.field) return filteredData;
    const { field, direction } = sortConfig;
    const type = getColumnType(field);

    return [...filteredData].sort((a, b) => {
      const av = a[field] ?? '';
      const bv = b[field] ?? '';

      if (av === '' && bv !== '') return direction === 'asc' ? 1 : -1;
      if (av !== '' && bv === '') return direction === 'asc' ? -1 : 1;
      if (av === '' && bv === '') return 0;

      if (type === 'text') {
        return direction === 'asc'
          ? String(av).localeCompare(String(bv), undefined, { numeric: true })
          : String(bv).localeCompare(String(av), undefined, { numeric: true });
      }

      return direction === 'asc' ? (av - bv) : (bv - av);
    });
  }, [filteredData, sortConfig, enableSorting]);

  // Pagination
  const isServerPaginated = Boolean(serverPagination);

  const safePageCount = isServerPaginated
    ? Math.max(0, Math.ceil(serverTotalRows / rowsPerPage) - 1)
    : Math.max(0, Math.ceil(sortedData.length / rowsPerPage) - 1);
    
  const safePage = isServerPaginated ? page : Math.min(page, safePageCount);

  const paginatedData = useMemo(() => {
    if (isServerPaginated) return sortedData;
    if (isMobile) return sortedData; 
    return sortedData.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage);
  }, [sortedData, safePage, rowsPerPage, isMobile, isServerPaginated]);

  return {
    page: safePage,
    setPage,
    expandedRowKey,
    setExpandedRowKey,
    isPaginationChange,
    setIsPaginationChange,
    paginatedData,
    sortedData,
    safePageCount,
    orderedKeysDesktop,
  };
};
